
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { purePmInEditorDirectStreamingPluginKey as streamingPluginKey } from '../pluginKey';
import { updateLoadingPositions } from '../transforms';
import type { PurePmInEditorDirectStreamingPluginState } from '../types';

import type { StreamingViewState } from './streamingViewState';

const LOADING_INTERVAL_MIN = 80;
const LOADING_INTERVAL_MAX = 240;

/**
 * Manages the loading animation during streaming mount/initialization phase.
 * Schedules/cancels loading animation based on streaming state.
 */
export function handleLoadingAnimation(
	view: EditorView,
	state: PurePmInEditorDirectStreamingPluginState,
	viewState: StreamingViewState,
) {
	const shouldAnimate =
		(state.streamPhase === 'mounting' || state.streamPhase === 'streaming') &&
		!state.isPaused &&
		state.aiContentEndPos === undefined;

	if (shouldAnimate && !viewState.loadingTimeout) {
		scheduleNextLoadingTick(view, viewState);
	}

	if (!shouldAnimate && viewState.loadingTimeout) {
		cancelLoadingAnimation(viewState);
	}
}

/**
 * Schedules the next loading animation tick with a random interval.
 * Recursively calls itself to create a continuous animation loop.
 */
export function scheduleNextLoadingTick(view: EditorView, viewState: StreamingViewState) {
	const interval =
		Math.random() * (LOADING_INTERVAL_MAX - LOADING_INTERVAL_MIN) + LOADING_INTERVAL_MIN;

	viewState.loadingTimeout = window.setTimeout(() => {
		const state = streamingPluginKey.getState(view.state);

		if (
			!state ||
			state.isPaused ||
			state.aiContentEndPos !== undefined ||
			state.streamPhase === 'idle'
		) {
			viewState.loadingTimeout = undefined;
			return;
		}

		try {
			view.dispatch(updateLoadingPositions(view.state.tr));
		} catch {
			// Loading position update failed - continue
		}

		// Schedule next tick with NEW random interval
		scheduleNextLoadingTick(view, viewState);
	}, interval);
}

/**
 * Cancels the loading animation by clearing the timeout.
 */
export function cancelLoadingAnimation(viewState: StreamingViewState) {
	if (viewState.loadingTimeout) {
		clearTimeout(viewState.loadingTimeout);
		viewState.loadingTimeout = undefined;
	}
}
